import React, { useEffect, useMemo, useState } from 'react';
import './App.css';
import './humor.css';
import Header from './components/Header';
import HeroMessage from './components/HeroMessage';
import OverviewCards from './components/OverviewCards';
import SummerCountdown from './components/SummerCountdown';
import HolidayTimeline from './components/HolidayTimeline';
import HumorSection from './components/HumorSection';
import SettingsDialog from './components/SettingsDialog';
import Celebration from './components/Celebration';
import Footer from './components/Footer';
import { useLocalSettings } from './hooks/useLocalSettings';
import { useCelebration } from './hooks/useCelebration';
import { getRegion } from './data/regions';
import {
  daysUntil,
  getCurrentHoliday,
  getFutureSummer,
  getNextHoliday,
  getRemainingFreeDays,
  getUpcomingHolidays
} from './utils/holidayLogic';
import {
  countSchoolDays,
  getMonthProgress,
  isPersonalSchoolDay
} from './utils/schoolDayLogic';
import { getCountdown } from './utils/countdown';
import { isSameDay } from './utils/dateUtils';

function getPageState(now, currentHoliday, nextHoliday, settings) {
  const weekend = now.getDay() === 0 || now.getDay() === 6;
  const firstHolidayDay = currentHoliday
    && isSameDay(now, currentHoliday.startDate);

  if (firstHolidayDay) {
    return {
      eyebrow: currentHoliday.name,
      title: 'Ferienanfang – yippie!',
      text: 'Jetzt beginnt deine wohlverdiente freie Zeit. Durchatmen, auftanken und genießen!'
    };
  }

  if (currentHoliday) {
    return {
      eyebrow: currentHoliday.name,
      title: `Immer noch ${getRemainingFreeDays(now, currentHoliday)} freie Tage!`,
      text: 'Herrlich: Deine Ferien laufen. Heute gehört die Zeit ganz dir.'
    };
  }

  if (weekend) {
    return {
      eyebrow: 'Deine Auszeit',
      title: 'Wochenende – Zeit für dich!',
      text: 'Genieße den Tag, sammle schöne Momente und neue Energie.'
    };
  }

  if (isPersonalSchoolDay(now, settings.workdays, settings.regionId)
    && now.getHours() >= 15) {
    return {
      eyebrow: 'Ein guter Tagesmoment',
      title: 'Feierabend – heute wieder viel geschafft!',
      text: 'Du hast heute viel bewegt. Jetzt darf der Tag ganz leicht ausklingen.'
    };
  }

  const calendarDays = nextHoliday
    ? daysUntil(now, nextHoliday.startDate)
    : 0;

  return {
    eyebrow: nextHoliday?.name || 'Vorfreude',
    title: nextHoliday
      ? `Nur noch ${calendarDays} Tage!`
      : 'Neue Ferientermine folgen bald!',
    text: `${getRegion(settings.regionId).name} ist wieder einen Tag näher an deiner nächsten wohlverdienten Auszeit.`
  };
}

function App() {
  const [now, setNow] = useState(() => new Date());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useLocalSettings();

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('./sw.js').catch(() => {});
    }

    return () => window.clearInterval(timer);
  }, []);

  const currentHoliday = getCurrentHoliday(now, settings.regionId);
  const nextHoliday = getNextHoliday(now, settings.regionId);
  const futureSummer = getFutureSummer(now, settings.regionId);
  const pageState = getPageState(
    now,
    currentHoliday,
    nextHoliday,
    settings
  );

  const summerCountdown = futureSummer
    ? getCountdown(now, futureSummer.startDate)
    : { days: 0, hours: 0, minutes: 0, seconds: 0 };

  const automaticEvent = useMemo(() => {
    if (
      currentHoliday
      && isSameDay(now, currentHoliday.startDate)
    ) {
      return {
        type: `holiday-start-${currentHoliday.id}`,
        title: 'Ferienanfang – yippie!',
        text: 'Deine freie Zeit beginnt!'
      };
    }

    if (!currentHoliday && (now.getDay() === 0 || now.getDay() === 6)) {
      return {
        type: 'weekend',
        title: 'Wochenende – Zeit für dich!',
        text: 'Ein schöner Moment für neue Energie.'
      };
    }

    if (
      !currentHoliday
      && settings.workdays.includes(now.getDay())
      && now.getHours() >= 15
    ) {
      return {
        type: 'after-work',
        title: 'Heute viel bewegt!',
        text: 'Jetzt beginnt dein wohltuender Feierabend.'
      };
    }

    return null;
  }, [
    currentHoliday?.id,
    now.getDay(),
    now.getHours(),
    settings.workdays
  ]);

  const { celebration, play, close } = useCelebration(
    now,
    automaticEvent
  );

  const nextSchoolDays = countSchoolDays(
    now,
    nextHoliday?.startDate,
    settings.workdays,
    settings.regionId
  );

  const summerSchoolDays = countSchoolDays(
    now,
    futureSummer?.startDate,
    settings.workdays,
    settings.regionId
  );

  const replayJoy = () => play({
    type: 'manual',
    title: pageState.title,
    text: currentHoliday
      ? 'Genieße deine freie Zeit!'
      : 'Vorfreude macht jeden Tag ein bisschen heller.'
  });

  return (
    <div className="app-shell">
      <Header
        onSettings={() => setSettingsOpen(true)}
        onCelebrate={replayJoy}
      />

      <main>
        <HeroMessage {...pageState} name={settings.firstName.trim()} />
        <OverviewCards
          nextSchoolDays={nextSchoolDays}
          summerSchoolDays={summerSchoolDays}
          monthProgress={getMonthProgress(now)}
        />
        <SummerCountdown
          countdown={summerCountdown}
          target={futureSummer}
          summerIsActive={currentHoliday?.type === 'summer'}
        />
        <HolidayTimeline
          holidays={getUpcomingHolidays(now, settings.regionId)}
          currentHolidayId={currentHoliday?.id}
        />
        <HumorSection />

        <aside className="privacy-banner">
          <strong>Deine Daten bleiben bei dir.</strong>
          <span>
            Nur Vorname, Bundesland und Arbeitstage werden auf diesem Gerät
            gespeichert.
          </span>
        </aside>
      </main>

      <Footer />

      <SettingsDialog
        open={settingsOpen}
        settings={settings}
        onChange={setSettings}
        onClose={() => setSettingsOpen(false)}
      />

      <Celebration celebration={celebration} onClose={close} />
    </div>
  );
}

export default App;